import { pushDebug } from './debugBus';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
export const APP_ENV = import.meta.env.VITE_APP_ENV || 'development';
export const ROUTING_MODE = import.meta.env.VITE_ROUTING_MODE || 'subpath';

// Resolve a boolean VITE flag: an explicit 'true'/'false' wins; when the var is
// unset/empty the provided default (usually APP_DEBUG) decides.
function viteBool(value: string | undefined, defaultValue: boolean): boolean {
  if (value === undefined || value === '') return defaultValue;
  return value === 'true';
}

// APP_DEBUG — master debug switch for the public site (default true). Mirrors the
// backend APP_DEBUG: enables the on-screen DebugConsole, detailed error views and
// API console logging unless a specific flag is set explicitly.
export const APP_DEBUG = viteBool(import.meta.env.VITE_APP_DEBUG, true);
export const ENABLE_CONSOLE_LOGS = viteBool(import.meta.env.VITE_ENABLE_CONSOLE_LOGS, APP_DEBUG);
export const DEBUG_MODE = viteBool(import.meta.env.VITE_DEBUG_MODE, APP_DEBUG);
export const ENABLE_DETAILED_ERRORS = viteBool(import.meta.env.VITE_ENABLE_DETAILED_ERRORS, APP_DEBUG);

type RequestInterceptor = (config: RequestInit, url: string) => RequestInit | Promise<RequestInit>;
type ResponseInterceptor = (response: Response, data: unknown) => unknown | Promise<unknown>;

interface Interceptors {
  request: RequestInterceptor[];
  response: ResponseInterceptor[];
}

const interceptors: Interceptors = {
  request: [],
  response: [],
};

// ── CSRF token handling ───────────────────────────────────────────────────────
// The backend enforces CSRF on all cookie-authenticated, state-changing requests
// (see backend/src/middleware/csrf.ts). Anonymous citizen flows on the public
// site still need a token because they share the same global middleware. We
// fetch one from GET /api/public/csrf-token on first POST and reuse it for the
// session. The cookie is set by the backend (SameSite=Strict, non-httpOnly) and
// the header is attached by `attachCsrfHeader()` below.

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const target = `${name}=`;
  const cookies = document.cookie ? document.cookie.split(';') : [];
  for (const raw of cookies) {
    const trimmed = raw.trim();
    if (trimmed.startsWith(target)) {
      return decodeURIComponent(trimmed.substring(target.length));
    }
  }
  return null;
}

let cachedCsrfToken: string | null = null;
let csrfFetchInFlight: Promise<string | null> | null = null;

async function fetchCsrfTokenOnce(): Promise<string | null> {
  if (cachedCsrfToken) return cachedCsrfToken;
  const cookieToken = readCookie('csrf_token');
  if (cookieToken) {
    cachedCsrfToken = cookieToken;
    return cookieToken;
  }
  if (csrfFetchInFlight) return csrfFetchInFlight;
  csrfFetchInFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/public/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) return null;
      const body = (await res.json()) as { data?: { csrfToken?: string } };
      const token = body?.data?.csrfToken ?? readCookie('csrf_token');
      if (token) cachedCsrfToken = token;
      return token ?? null;
    } catch {
      return null;
    } finally {
      csrfFetchInFlight = null;
    }
  })();
  return csrfFetchInFlight;
}

/** Resets the cached CSRF token (e.g. after a 403 CSRF_TOKEN_INVALID). */
function invalidateCsrfToken(): void {
  cachedCsrfToken = null;
}

class ApiClient {
  defaults: { headers: Record<string, string> } = {
    headers: {},
  };

  async request<T>(endpoint: string, options: RequestInit = {}, _isRetry = false): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    let headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.defaults.headers,
      ...((options.headers as Record<string, string>) || {}),
    };

    const method = (options.method || 'GET').toUpperCase();
    // Attach CSRF token on state-changing requests. Skip if caller already supplied
    // an Authorization header (bearer-token APIs bypass CSRF on the backend).
    const hasAuthHeader = Object.keys(headers).some(k => k.toLowerCase() === 'authorization');
    if (UNSAFE_METHODS.has(method) && !hasAuthHeader) {
      const token = await fetchCsrfTokenOnce();
      if (token) {
        headers['X-CSRF-Token'] = token;
      }
    }

    let config: RequestInit = {
      ...options,
      headers,
      // Send the csrf_token cookie back with the request so the double-submit
      // check on the backend can compare cookie vs header.
      credentials: options.credentials ?? 'include',
    };

    for (const interceptor of interceptors.request) {
      config = await interceptor(config, url);
    }

    const response = await fetch(url, config);

    let data: unknown;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      const envelope = data as { error?: { code?: string; message?: string; details?: unknown }; message?: string } | null;
      const code = envelope?.error?.code;

      // If the CSRF cookie expired between page load and submission, refetch and
      // retry once before surfacing the error to the user.
      if (
        !_isRetry &&
        response.status === 403 &&
        (code === 'CSRF_TOKEN_MISSING' || code === 'CSRF_TOKEN_INVALID') &&
        UNSAFE_METHODS.has(method)
      ) {
        invalidateCsrfToken();
        return this.request<T>(endpoint, options, true);
      }

      // Provide specific, actionable error messages for common failure modes so
      // users see a meaningful message instead of "Request failed".
      let message: string;
      if (response.status === 0) {
        // Network failure — backend unreachable
        message = 'Unable to connect to the server. Please check your internet connection.';
      } else if (response.status === 502 || response.status === 503) {
        message = 'Server is temporarily unavailable. Please try again in a few moments.';
      } else if (response.status === 504) {
        message = 'Request timed out. Please try again.';
      } else if (response.status >= 500) {
        message = 'An unexpected server error occurred. Please try again later.';
      } else if (response.status === 401) {
        message = 'Session expired. Please sign in again.';
      } else if (response.status === 429) {
        message = 'Too many requests. Please wait a moment and try again.';
      } else {
        // 4xx — prefer backend error message, fall back to generic
        message = envelope?.error?.message ?? envelope?.message ?? 'Request failed';
      }

      const error = new Error(message) as Error & { status?: number; code?: string; details?: unknown };
      error.status = response.status;
      if (code) error.code = code;
      if (envelope?.error?.details !== undefined) error.details = envelope.error.details;

      // Record the failure in the on-screen debug console with the full server response.
      if (DEBUG_MODE) {
        pushDebug(
          'error',
          'api',
          `${response.status} ${method} ${endpoint} — ${message}`,
          JSON.stringify({ code, status: response.status, response: data }, null, 2)
        );
      }

      throw error;
    }

    for (const interceptor of interceptors.response) {
      data = await interceptor(response, data);
    }

    return data as T;
  }

  get<T>(endpoint: string) {
    return this.request<T>(endpoint);
  }

  post<T>(endpoint: string, body: unknown) {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  addRequestInterceptor(interceptor: RequestInterceptor) {
    interceptors.request.push(interceptor);
    return () => {
      const index = interceptors.request.indexOf(interceptor);
      if (index > -1) interceptors.request.splice(index, 1);
    };
  }

  addResponseInterceptor(interceptor: ResponseInterceptor) {
    interceptors.response.push(interceptor);
    return () => {
      const index = interceptors.response.indexOf(interceptor);
      if (index > -1) interceptors.response.splice(index, 1);
    };
  }
}

export const api = new ApiClient();

// Public API endpoints
export interface PublicStats {
  categories: number;
  inspections: number;
  wards: number;
  users: number;
  totalReports: number;
  resolvedReports: number;
  avgCleanlinessScore: number;
}

export interface PublicCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
}

export interface PublicFeature {
  id: number;
  title: string;
  description: string;
  icon: string;
  color: string;
}

export interface PublicWorkflowStep {
  id: number;
  number: string;
  title: string;
  description: string;
  icon: string;
}

export interface PublicBenefit {
  id: number;
  title: string;
  description: string;
  icon: string;
}

export interface PublicAICapability {
  id: number;
  title: string;
}

export interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  organization?: string;
  subject?: string;
  message: string;
}

export interface ComplaintFormData {
  citizenName: string;
  phone: string;
  email?: string;
  city: string;
  wardArea: string;
  address: string;
  category: string;
  subject: string;
  description: string;
  imageUrl?: string;
  latitude?: number;
  longitude?: number;
}


export interface HealthCheckResponse {
  status: string;
  timestamp?: string;
  uptime?: number;
}

export interface ComplaintStatus {
  trackingId: string;
  trackingNumber?: string; // alias
  status: string;
  category: string;
  area: string;
  city: string;
  submittedAt: string;
  lastUpdatedAt?: string;
  description?: string;
  imageUrl?: string;
  citizenResponse?: string;
  rejectionReason?: string;
  timeline?: Array<{ status: string; message: string; timestamp: string }>;
}

export interface APIResponse<T> {
  data?: T;
  success?: boolean;
  message?: string;
}

export const publicApi = {
  // Stats
  getStats: () => api.get<APIResponse<PublicStats>>('/public/stats'),

  // Contact form — accepts optional idempotency key to prevent double-submit
  submitContact: (data: ContactFormData, idempotencyKey?: string) => {
    const extraHeaders: Record<string, string> = {};
    if (idempotencyKey) extraHeaders['X-Idempotency-Key'] = idempotencyKey;
    return api.request<APIResponse<{ submitted: boolean }>>('/public/contact', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: extraHeaders,
    });
  },

  // Categories
  getCategories: () => api.get<APIResponse<PublicCategory[]>>('/public/categories'),

  // Features
  getFeatures: () => api.get<APIResponse<PublicFeature[]>>('/public/features'),

  // Workflow
  getWorkflow: () => api.get<APIResponse<PublicWorkflowStep[]>>('/public/workflow'),

  // Benefits
  getBenefits: () => api.get<APIResponse<PublicBenefit[]>>('/public/benefits'),

  // AI Capabilities
  getAICapabilities: () => api.get<APIResponse<PublicAICapability[]>>('/public/ai-capabilities'),

  // Complaints - Submit new complaint
  // The form's category <select> uses backend enum values directly (garbage_overflow, clogged_drain, etc.)
  // so no mapping is needed — pass the value through unchanged.
  submitComplaint: (data: ComplaintFormData, idempotencyKey?: string) => {
    const extraHeaders: Record<string, string> = {};
    if (idempotencyKey) extraHeaders['X-Idempotency-Key'] = idempotencyKey;

    // Build description: prepend subject line if provided
    const fullDescription = [
      data.subject ? `Subject: ${data.subject}` : '',
      data.description,
    ].filter(Boolean).join('\n\n') || 'No description provided';

    const backendPayload = {
      name: data.citizenName,
      mobile: data.phone.replace(/\D/g, '').slice(-10),
      email: data.email || undefined,
      city: data.city,
      area: data.wardArea || data.address || data.city,
      category: data.category || 'other',
      description: fullDescription,
      imageUrl: data.imageUrl || undefined,
      latitude: data.latitude != null ? data.latitude : undefined,
      longitude: data.longitude != null ? data.longitude : undefined,
    };
    return api.request<APIResponse<{ trackingId: string; reportId: string; status: string; message: string }>>('/public/complaints', {
      method: 'POST',
      body: JSON.stringify(backendPayload),
      headers: extraHeaders,
    });
  },

  // Complaints - Track by tracking number
  trackComplaint: (trackingNumber: string) => api.get<APIResponse<ComplaintStatus>>(`/public/complaints/${trackingNumber}`),

  // Upload complaint image (public, no auth, rate-limited per IP)
  // Returns { url: string } inside the data envelope
  uploadComplaintImage: (imageData: string) =>
    api.post<APIResponse<{ url: string }>>('/public/uploads/complaint-image', { imageData }),

  // Complaint categories (canonical list, no auth required)
  getComplaintCategories: () =>
    api.get<APIResponse<Array<{ key: string; label: string; description: string; color: string }>>>('/public/complaint-categories'),

  // Phone + OTP complaint tracking: step 1 — request OTP
  requestComplaintTrackOtp: (phone: string) =>
    api.post<APIResponse<{ message: string; expiresInMinutes: number }>>('/public/complaints/track/request-otp', { phone }),

  // Phone + OTP complaint tracking: step 2 — verify OTP, get scoped track token
  verifyComplaintTrackOtp: (phone: string, otp: string) =>
    api.post<APIResponse<{ trackToken: string; expiresInSeconds: number }>>('/public/complaints/track/verify-otp', { phone, otp }),

  // Phone + OTP complaint tracking: step 3 — list complaints for this phone (requires trackToken)
  getComplaintsByPhone: (trackToken: string) =>
    api.request<APIResponse<{
      phone: string;
      complaints: Array<{
        trackingId: string;
        category: string;
        description: string;
        city: string;
        area: string;
        status: string;
        citizenResponse: string | null;
        rejectionReason: string | null;
        imageUrl: string | null;
        submittedAt: string;
        lastUpdatedAt: string;
      }>;
    }>>('/public/complaints', {
      method: 'GET',
      headers: { Authorization: `Bearer ${trackToken}` },
    }),

  // Health check — used to verify backend reachability
  getHealth: () => api.get<HealthCheckResponse>('/health'),

  // Public configuration (support email, feature flags, etc.)
  getPublicConfig: () => api.get<APIResponse<{ supportEmail?: string; helplineNumber?: string; supportPhone?: string }>>('/public/config'),
};
